import React, { useState } from 'react';

import { notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getMoment, getNow, showDateNoTime } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { forceReloadCreditNote, useClearCreditNoteConfirm } from '@hooks/API/UseCreditNotes';

import { NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import PrintCreditNoteButton from '../PrintCreditNoteButton';
import CreditNoteConfirmDrawer from './Drawers/CreditNoteConfirmDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    padding: 0.25rem 0 0.5rem 0;

    .creditnote-top-box {
        display: flex;
        padding-right: 2rem;
        align-items: center;
        justify-content: flex-start;
        flex: 1 1 0;

        .creditnote-top-box-icon {
            min-width: 4rem;
            text-align: center;
            font-size: 1.75rem;
        }

        .creditnote-top-box-content {
            .creditnote-top-box-content-title {
                font-weight: bold;
                font-size: ${(props) => props.theme.FontSizeBase};
            }
            .creditnote-top-box-content-label {
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
            }

            p {
                margin: 0;
            }
            small {
                max-width: 13rem;
                font-size: ${(props) => props.theme.FontSizeSm};
                display: block;
                opacity: 0.5;
                line-height: 1.1em;
            }
        }
    }

    .creditnote-top-export {
        display: flex;
        min-width: 12rem;
        gap: 2rem;
    }

    .confirmation-payment-and-export {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-bottom: 1rem;
    }
`;

const CreditNotePageTop = ({ creditNoteData, locked }) => {
    const canPost = useUserPermission('PostInvoice');

    const [showCreditNoteConfirmDrawer, setShowCreditNoteConfirmDrawer] = useState(false);

    const { mutateAsync: onClearCreditNoteConfirm } = useClearCreditNoteConfirm();

    const onClearCreditNote = async () => {
        try {
            await onClearCreditNoteConfirm(creditNoteData.ID);
        } catch (e) {
            notification.error({
                message: 'Error unconfirming credit note',
                description: e?.message || 'Unknown error',
            });
        }
    };

    const setAsReadyToPost = async () => {
        try {
            await Axios.put(`/CreditNotes/SetReadyToPost`, { CreditNoteID: creditNoteData.ID, ReadyToPost: true });
            forceReloadCreditNote(creditNoteData.ID);
        } catch (e) {
            notification.error({
                message: 'Error setting credit note as ready to post',
                description: e?.message || 'Unknown error',
            });
        }
    };

    return (
        <Wrapper>
            <div className="confirmation-payment-and-export">
                <div className="creditnote-top-box">
                    <div className="creditnote-top-box-icon">
                        <NwIcon
                            icon={byPrefixAndName.fasl['file-invoice']}
                            color={creditNoteData.Number === null ? StyleVariables.DangerColor : '#000'}
                        />
                    </div>
                    {!creditNoteData.Number ? (
                        <div className="creditnote-top-box-content">
                            <div className="creditnote-top-box-content-title">Not confirmed</div>
                            <NwLinkButton
                                disabled={locked}
                                onClick={() => setShowCreditNoteConfirmDrawer(true)}
                                label="confirm and assign credit note number"
                            />
                        </div>
                    ) : (
                        <div className="creditnote-top-box-content">
                            <div className="creditnote-top-box-content-title">
                                {creditNoteData.Number} / {getMoment(creditNoteData.Date).year()}{' '}
                            </div>
                            <div>Credit note date: {getMoment(creditNoteData.Date).format('DD MMM YYYY')} </div>
                            <NwLinkButton
                                disabled={locked}
                                onClick={() => onClearCreditNote()}
                                label="clear credit note confirmation"
                            />
                        </div>
                    )}
                </div>
                <div className="creditnote-top-export">
                    <div className="creditnote-top-export-icon">
                        <PrintCreditNoteButton
                            creditNoteID={creditNoteData.ID}
                            creditNoteType={creditNoteData.TypeName}
                            placement="leftTop"
                        />
                    </div>
                    <div className="creditnote-top-export-icon">
                        <NwIcon
                            disabled={locked || !canPost}
                            size="2x"
                            icon={byPrefixAndName.fasl['file-export']}
                            onClick={setAsReadyToPost}
                            tooltip="set this credit note as ready to post"
                            pointer
                        />
                    </div>
                    <div className="creditnote-top-export-icon">
                        <NwIcon disabled size="2x" icon={byPrefixAndName.fasl['cloud-arrow-down']} />
                    </div>
                </div>
            </div>
            {showCreditNoteConfirmDrawer && (
                <CreditNoteConfirmDrawer
                    creditNoteID={creditNoteData.ID}
                    onClose={() => setShowCreditNoteConfirmDrawer(false)}
                />
            )}
        </Wrapper>
    );
};

export default CreditNotePageTop;
